<template>
    <div class="status-bar px-4" :class="{ 'global-status-bar': !isLockstepRoute }" v-if="client && client.is_onboarding ">
        <nav class="navbar navbar-expand primary-tab-bar w-100 mb-4">
            <div class="container-fluid d-flex flex-wrap justify-content-between align-items-center">
                <div class="d-flex align-items-center flex-shrink-0">
                    <img src="/img/images/lockstep-status-bar-icon.png" alt="Lockstep Status Bar Icon" class="lockstep-status-bar-icon ms-3" style="width: 40px; height: 40px;">
                    <span class="ms-2 fw-bold">Next Step:</span>
                    <a class="ms-sm-2 ms-3" v-if="nextTask"
                       :href="nextTask.url" 
                       :target="isExternalUrl(nextTask.url) ? '_blank' : '_self'"
                       :rel="isExternalUrl(nextTask.url) ? 'noopener noreferrer' : ''">
                        {{ nextTask.name }}
                        <i v-if="isExternalUrl(nextTask.url)" class="fas fa-external-link-alt ms-1" style="font-size: 0.85em;"></i>
                    </a>
                    <span class="ms-3" v-else>No remaining tasks</span>
                </div>

                <div class="status-bar-right d-flex justify-content-end align-items-center mt-3 mt-md-0">
                    <div class="progress-circle-container d-flex align-items-center">
                        <div class="progress-circle me-2">
                            <svg class="progress-ring" width="50" height="50">
                                <circle
                                    class="progress-ring-background"
                                    cx="25"
                                    cy="25"
                                    r="20"
                                    fill="transparent"
                                    stroke="#e9ecef"
                                    stroke-width="4"
                                />
                                <circle
                                    class="progress-ring-progress"
                                    cx="25"
                                    cy="25"
                                    r="20"
                                    fill="transparent"
                                    stroke="#007bff"
                                    stroke-width="4"
                                    :stroke-dasharray="circumference"
                                    :stroke-dashoffset="strokeDashoffset"
                                />
                            </svg>
                            <span class="progress-text">{{ progress }}%</span>
                        </div>
                        <div class="progress-labels">
                            <span class="status-label">Onboarding</span>
                            <span class="completed-label">Completed</span>
                        </div>
                    </div>
                </div>
            </div>
        </nav>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useLockstepStore } from '../../stores/lockstep';
import { useClientStore } from '../../stores/client';

export default defineComponent({
    name: 'StatusBar',
    data() {
        return {
            lockstepStore: useLockstepStore(),
            clientStore: useClientStore()
        };
    },
    computed: {
        status() {
            return 'onboarding';
        },

        client() {
            return this.clientStore.getClient;
        },

        // Check if current route is on lockstep
        isLockstepRoute() {
            return window.location.pathname.includes('/lockstep/');
        },
        // Get the next incomplete task (tasks are already sorted by due date from controller)
        nextTask() {
            return (this.clientStore.getTasks || []).find(task => !task.is_completed);
        },
        
        // Percent of tasks complete
        progress() {
            const tasks = this.clientStore.getTasks || [];
            if (tasks.length === 0) return 0;
            return Math.round((tasks.filter(task => task.is_completed === true).length / tasks.length) * 100);
        },
        
        // Calculate circumference for SVG circle
        circumference() {
            return 2 * Math.PI * 20; // radius of 20
        },
        
        // Calculate stroke dash offset for progress animation
        strokeDashoffset() {
            return this.circumference - (this.progress / 100) * this.circumference;
        }
    },
    methods: {
        // Check if URL is external (different domain)
        isExternalUrl(url) {
            if (!url) return false;
            try {
                const urlObj = new URL(url, window.location.origin);
                return urlObj.origin !== window.location.origin;
            } catch (e) {
                // If URL parsing fails, assume it's internal
                return false;
            }
        },

        // Initialize client from server-injected window.client if available
        initializeClientFromWindow() {
            if (window.client && !this.clientStore.getClient) {
                try {
                    const permissions = window.userAccessPermissions || {};
                    const tasks = window.client.tasks || [];
                    this.clientStore.initialize(window.client, permissions, tasks);
                } catch (e) {
                    // no-op
                }
            }
        },

        // Attempt to hydrate client by parsing the URL and hitting API endpoints
        async initializeClientFromUrlIfMissing() {
            if (this.clientStore.getClient) return;

            const segments = window.location.pathname.split('/').filter(Boolean);
            // We expect client URL/UUID at second segment position
            const clientUrl = segments.length >= 2 ? segments[1] : null;
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{4}-[0-9a-f]{12}$/i;
            if (!clientUrl || !uuidRegex.test(clientUrl)) return;

            try {
                // 1) Get permissions (binds Client by url) to retrieve access
                const permRes = await fetch(`/api/permissions/${clientUrl}`);
                if (!permRes.ok) return;
                const permData = await permRes.json();
                const permissions = permData.permissions || {};

                // 2) Load client by URL
                const clientRes = await fetch(`/api/client/${clientUrl}`);
                if (!clientRes.ok) return;
                const clientPayload = await clientRes.json();
                const clientData = clientPayload.data || clientPayload;
                // Ensure url is set from the path if missing
                if (clientData && !clientData.url) clientData.url = clientUrl;

                this.clientStore.initialize(clientData, permissions, []);
            } catch (e) {
                // swallow errors – bar will stay hidden
            }
        },

        async ensureTasksLoaded() {
            if (!this.clientStore.getClient?.url) return;
            if (this.clientStore.getTasks.length > 0) return;
            await this.clientStore.fetchTasks();
        }
    }
    ,
    async mounted() {
        // 1) Use server-provided window.client if available
        this.initializeClientFromWindow();
        // 2) If still missing, hydrate via URL lookup
        await this.initializeClientFromUrlIfMissing();
        // 3) Load tasks (uses client.url)
        await this.ensureTasksLoaded();
    }
});
</script>
